import { useMemo, useState } from 'react';
import { useSubscriptions } from '../../api/funnel';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect, SearchInput } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { SubscriptionStatusBadge } from '../../components/Badge';
import { UserCell } from '../../components/Avatar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { useDebounced } from '../../hooks/useDebounced';
import { formatRelative } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { AdminSubscription, SubscriptionStatus } from '../../types/api';
import { SwapTutorModal } from './SwapTutorModal';

/**
 * How much of a bundle is left, as a bar.
 *
 * Three numbers matter and only two of them are obvious: taught, booked but
 * not yet taught, and genuinely remaining. Showing them stacked is what
 * stops "4 of 12" being read as "8 still bookable" when half of those are
 * already on the calendar.
 */
function LessonsBar({ subscription }: { subscription: AdminSubscription }) {
  const { lessonsTotal, lessonsUsed, lessonsScheduled } = subscription;
  const remaining = Math.max(0, lessonsTotal - lessonsUsed - lessonsScheduled);
  const pct = (n: number) => (lessonsTotal > 0 ? (n / lessonsTotal) * 100 : 0);

  return (
    <div className="min-w-[8rem]">
      <div
        className="flex h-1.5 overflow-hidden rounded-full bg-surface-2"
        role="img"
        aria-label={`${lessonsUsed} taught, ${lessonsScheduled} booked, ${remaining} left of ${lessonsTotal}`}
      >
        <span className="bg-good" style={{ width: `${pct(lessonsUsed)}%` }} />
        <span className="bg-accent" style={{ width: `${pct(lessonsScheduled)}%` }} />
      </div>
      <p className="mt-1 text-xs text-ink-3">
        {lessonsUsed} taught · {lessonsScheduled} booked · {remaining} left
      </p>
    </div>
  );
}

export function SubscriptionsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SubscriptionStatus | ''>('active');
  const [page, setPage] = useState(1);
  const [swapping, setSwapping] = useState<AdminSubscription | null>(null);

  const debouncedSearch = useDebounced(search);

  const query = useMemo(
    () => ({
      page,
      limit: 20,
      status: status || undefined,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, status, debouncedSearch],
  );

  const subscriptions = useSubscriptions(query);
  const hasFilters = Boolean(debouncedSearch || status);

  return (
    <Page>
      <PageHeader
        title="Subscriptions"
        description="Bought bundles, who teaches them, and how much of each is left."
      />

      <div className="panel overflow-hidden">
        <FilterBar
          onReset={() => {
            setSearch('');
            setStatus('');
            setPage(1);
          }}
          hasFilters={hasFilters}
        >
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search the parent"
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            placeholder="Any status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        </FilterBar>

        {subscriptions.isError ? (
          <ErrorState
            message={
              subscriptions.error instanceof ApiError
                ? subscriptions.error.message
                : undefined
            }
            onRetry={() => void subscriptions.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Parent</th>
                    <th>Bundle</th>
                    <th>Tutor</th>
                    <th>Lessons</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.isLoading ? (
                    <SkeletonRows rows={8} cols={7} />
                  ) : subscriptions.data && subscriptions.data.data.length > 0 ? (
                    subscriptions.data.data.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <UserCell name={s.student.fullName} />
                          <span className="mt-0.5 block text-xs text-ink-3">
                            {s.student.email}
                          </span>
                        </td>
                        <td className="text-xs text-ink-2">
                          <span className="block text-ink">{s.bundle.nameEn}</span>
                          {s.originTrialRequestId && (
                            <span className="block text-ink-3">Came from a trial</span>
                          )}
                        </td>
                        <td className="text-xs text-ink-2">
                          {s.tutor ? (
                            <>
                              <span className="block text-ink">{s.tutor.fullName}</span>
                              {s.tutorHistory.length > 1 && (
                                <span className="block text-ink-3">
                                  {s.tutorHistory.length - 1} previous
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-ink-3">Not assigned</span>
                          )}
                        </td>
                        <td>
                          <LessonsBar subscription={s} />
                        </td>
                        <td>
                          <SubscriptionStatusBadge status={s.status} />
                        </td>
                        <td className="text-xs text-ink-3">
                          <span title={new Date(s.startedAt).toLocaleString()}>
                            {formatRelative(s.startedAt)}
                          </span>
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={s.status !== 'active'}
                            onClick={() => setSwapping(s)}
                          >
                            Swap tutor
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          title="No subscriptions here"
                          description="A subscription appears once a parent pays for a bundle."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {subscriptions.data && (
              <Pagination meta={subscriptions.data.meta} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {swapping && (
        <SwapTutorModal
          key={swapping.id}
          subscription={swapping}
          onClose={() => setSwapping(null)}
        />
      )}
    </Page>
  );
}
